import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { RiUserLine, RiMailLine, RiPhoneLine, RiSaveLine } from '@remixicon/react';
import { Button } from '@marine/ui/button';
import { Input } from '@marine/ui/input';
import { Label } from '@marine/ui/label';
import { Textarea } from '@marine/ui/textarea';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@marine/ui/card';
import { Avatar, AvatarFallback, AvatarImage } from '@marine/ui/avatar';
import { api } from '@marine/api-client/api';
import { toast } from 'sonner';
import { useAuth } from '@/hooks/useAuth';

const profileSchema = z.object({
  first_name: z.string().min(1, 'First name is required'),
  last_name: z.string().min(1, 'Last name is required'),
  email: z.string().email('Invalid email address'),
  phone: z.string().optional(),
  bio: z.string().max(500, 'Bio must be less than 500 characters').optional(),
  timezone: z.string().optional(),
  locale: z.string().optional(),
  photo: z.instanceof(FileList).optional(),
});

type ProfileFormData = z.infer<typeof profileSchema>;

export function ProfileInfoCard() {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  
  const form = useForm<ProfileFormData>({
    resolver: zodResolver(profileSchema),
    defaultValues: {
      first_name: user?.first_name || '',
      last_name: user?.last_name || '',
      email: user?.email || '',
      phone: user?.phone || '',
      bio: user?.bio || '',
      timezone: user?.timezone || 'UTC',
      locale: user?.locale || 'en',
    },
  });

  const updateProfile = useMutation({
    mutationFn: (data: ProfileFormData) => {
      const formData = new FormData();
      formData.append('_method', 'PUT'); // Laravel method spoofing for file uploads
      Object.entries(data).forEach(([key, value]) => {
        if (key === 'photo' && value instanceof FileList && value.length > 0) {
          formData.append('photo', value[0]);
        } else if (value) {
          formData.append(key, String(value));
        }
      });
      return api.post('/api/user/profile-information', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
    },
    onSuccess: () => {
      toast.success('Profile updated successfully');
      queryClient.invalidateQueries({ queryKey: ['auth-user'] });
      queryClient.invalidateQueries({ queryKey: ['me'] });
    },
    onError: (error: any) => {
      const message = error.response?.data?.message || 'Failed to update profile';
      toast.error(message);
    },
  });

  const onSubmit = (data: ProfileFormData) => {
    updateProfile.mutate(data);
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center gap-2">
          <RiUserLine className="h-5 w-5 text-muted-foreground" />
          <div>
            <CardTitle>Profile Information</CardTitle>
            <CardDescription>Update your personal information and avatar</CardDescription>
          </div>
        </div>
      </CardHeader>
      
      <CardContent>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
          {/* Avatar */}
          <div className="flex items-center gap-4">
            <Avatar className="h-20 w-20">
              <AvatarImage src={user?.avatar_url} alt={user?.full_name} />
              <AvatarFallback className="text-lg">
                {user?.first_name?.[0]}{user?.last_name?.[0]}
              </AvatarFallback>
            </Avatar>
            
            <div className="flex-1">
              <Label htmlFor="photo">Profile Photo</Label>
              <Input
                id="photo"
                type="file"
                accept="image/*"
                {...form.register('photo')}
                disabled={updateProfile.isPending}
                className="mt-1"
              />
              <p className="text-xs text-muted-foreground mt-1">
                JPG, PNG or GIF. Max 2MB.
              </p>
            </div>
          </div>

          {/* Name */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="first_name">First Name</Label>
              <Input
                id="first_name"
                {...form.register('first_name')}
                disabled={updateProfile.isPending}
              />
              {form.formState.errors.first_name && (
                <p className="text-sm text-red-500">{form.formState.errors.first_name.message}</p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="last_name">Last Name</Label>
              <Input
                id="last_name"
                {...form.register('last_name')}
                disabled={updateProfile.isPending}
              />
              {form.formState.errors.last_name && (
                <p className="text-sm text-red-500">{form.formState.errors.last_name.message}</p>
              )}
            </div>
          </div>

          {/* Contact */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <div className="relative">
                <RiMailLine className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  id="email"
                  type="email"
                  className="pl-10"
                  {...form.register('email')}
                  disabled={updateProfile.isPending}
                />
              </div>
              {form.formState.errors.email && (
                <p className="text-sm text-red-500">{form.formState.errors.email.message}</p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="phone">Phone</Label>
              <div className="relative">
                <RiPhoneLine className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  id="phone"
                  type="tel"
                  className="pl-10"
                  {...form.register('phone')}
                  disabled={updateProfile.isPending}
                />
              </div>
            </div>
          </div>

          {/* Bio */}
          <div className="space-y-2">
            <Label htmlFor="bio">Bio</Label>
            <Textarea
              id="bio"
              rows={3}
              placeholder="Tell us about yourself..."
              {...form.register('bio')}
              disabled={updateProfile.isPending}
            />
            <p className="text-xs text-muted-foreground">
              {form.watch('bio')?.length || 0}/500 characters
            </p>
          </div>

          <Button 
            type="submit" 
            disabled={updateProfile.isPending}
            className="w-full sm:w-auto"
          >
            <RiSaveLine className="h-4 w-4 mr-2" />
            {updateProfile.isPending ? 'Saving...' : 'Save Changes'}
          </Button>
        </form>
      </CardContent>
    </Card>
  );
}
